<script>
  import { createEventDispatcher } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faXmark, faBan, faCheck, faBuilding } from '@fortawesome/pro-light-svg-icons';

  import { effectiveAssignments } from '$utils/userAssignments.js';

  export let show = false;
  export let user = null; // For editing existing users
  export let isCreatingUser = false; // Explicit flag for creating vs editing
  export let callerIsAdmin = true;
  export let allowedProtocols = null;

  const dispatch = createEventDispatcher();
  // Using the proxy API endpoint pattern
  const API_BASE_URL = '/api/proxy';
  const MAX_STUDIES = 5;

  function emptyAssignment() {
    return {
      protocolNumber: '',
      siteIds: [],
      siteOptions: [],
      studyGUID: '',
      protocolError: '',
      loadingSites: false,
      allSites: true,
      siteFilter: ''
    };
  }

  let formData = {
    email: '',
    firstName: '',
    lastName: '',
    role: 'site',
    userType: 'Site',
    state: 'active',
    country: '',
    sponsor: '',
    assignments: [],
    canManageUsers: false,
    readOnly: false,
    blinded: false
  };
  let loading = false;
  let error = null;
  let success = false;
  let isEditing = false;
  let organizationStatus = [];
  let showOrganizationSection = false;
  let countryOptions = [];
  let countryNormalized = false;
  let studyOptions = [];
  let studiesLoading = false;

  function assignmentsFromUser(source) {
    return effectiveAssignments(source)
      .filter((row) => !Array.isArray(allowedProtocols) || allowedProtocols.includes(row.protocolNumber))
      .map((row) => ({
      ...emptyAssignment(),
      protocolNumber: row.protocolNumber,
      siteIds: [...row.siteIds],
      allSites: row.siteIds.length === 0,
      siteFilter: ''
    }));
  }

  function initializeForm() {
    if (user && !isCreatingUser) {
      // Editing existing user
      isEditing = true;
      formData = {
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.roles?.[0] || 'site',
        userType: user.userType || 'Site',
        state: user.state || 'active',
        country: user.country || '',
        sponsor: user.sponsor || '',
        assignments: assignmentsFromUser(user),
        canManageUsers: user.canManageUsers === true,
        readOnly: user.readOnly || false,
        blinded: user.blinded || false
      };
      formData.assignments.forEach((row, index) => {
        if (!rowLocked(row)) loadSitesForRow(index);
      });
    } else {
      // Creating new user
      isEditing = false;
      formData = {
        email: '',
        firstName: '',
        lastName: '',
        role: 'site',
        userType: 'Site',
        state: 'active',
        country: '',
        sponsor: '',
        assignments: [],
        canManageUsers: false,
        readOnly: false,
        blinded: false
      };
    }
    
    // Reset messages
    error = null;
    success = false;
    countryNormalized = false;
  }

  // Initialize form when modal is shown
  // Load country options BEFORE initializing form so the select has valid options
  // when formData.country is set, preventing Svelte from resetting the bound value.
  $: if (show) {
    openModal();
  }

  async function openModal() {
    await Promise.all([loadCountryOptions(), loadStudyOptions()]);
    initializeForm();
    if (user && !isCreatingUser) {
      fetchOrganizationStatus();
    }
  }

  async function loadCountryOptions() {
    try {
      const response = await fetch(`${API_BASE_URL}/lookups/COUNTRY`);
      if (response.ok) {
        const result = await response.json();
        countryOptions = result.data || [];
      }
    } catch (error) {
      console.error('Error loading country options:', error);
    }
  }

  async function loadStudyOptions() {
    studiesLoading = true;
    try {
      const response = await fetch(
        `${API_BASE_URL}/studies?pageSize=999&sortField=protocolNumber&sortOrder=asc`
      );
      if (response.ok) {
        const result = await response.json();
        const rows = Array.isArray(result.data) ? result.data : [];
        studyOptions = rows
          .filter((study) => study?.protocolNumber)
          .sort((a, b) =>
            String(a.protocolNumber).localeCompare(String(b.protocolNumber), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          );
      }
    } catch (err) {
      console.error('Error loading studies:', err);
      studyOptions = [];
    } finally {
      studiesLoading = false;
    }
  }

  function pickerStudies() {
    if (!Array.isArray(allowedProtocols)) return studyOptions;
    return studyOptions.filter((study) => allowedProtocols.includes(study.protocolNumber));
  }

  function rowLocked(row) {
    return Array.isArray(allowedProtocols) && !!row?.protocolNumber && !allowedProtocols.includes(row.protocolNumber);
  }

  function protocolOptionsFor(index) {
    const taken = new Set(
      formData.assignments
        .map((row, i) => (i === index ? '' : row.protocolNumber))
        .filter(Boolean)
    );
    const options = pickerStudies().filter((study) => !taken.has(study.protocolNumber));
    const current = formData.assignments[index]?.protocolNumber;
    if (current && !rowLocked(formData.assignments[index]) && !options.some((study) => study.protocolNumber === current)) {
      return [{ protocolNumber: current, studyName: current }, ...options];
    }
    return options;
  }

  function remainingStudyCount() {
    const taken = new Set(formData.assignments.map((row) => row.protocolNumber).filter(Boolean));
    return pickerStudies().filter((study) => !taken.has(study.protocolNumber)).length;
  }

  function visibleSites(assignment) {
    const options = assignment.siteOptions || [];
    const query = String(assignment.siteFilter || '').trim().toLowerCase();
    if (!query || options.length <= 8) return options;
    return options.filter((site) => `${site.siteId} ${site.siteName || ''}`.toLowerCase().includes(query));
  }

  // Ensure the bound country matches an available option once options are loaded.
  function normalizeCountryValue() {
    if (!formData.country || !Array.isArray(countryOptions) || countryOptions.length === 0) return;
    const current = String(formData.country).trim();
    // Try exact label match first
    let match = countryOptions.find((c) => String(c.label) === current);
    // Fallback: match by code
    if (!match) {
      match = countryOptions.find((c) => String(c.code) === current);
    }
    // Fallback: case-insensitive label match
    if (!match) {
      match = countryOptions.find((c) => String(c.label).toLowerCase() === current.toLowerCase());
    }
    if (match && formData.country !== match.label) {
      formData.country = match.label;
    }
    countryNormalized = true;
  }

  // Normalize once when options are available (or user changes) while modal is open
  $: if (show && countryOptions.length && !countryNormalized) {
    normalizeCountryValue();
  }

  function setRole(role) {
    formData = {
      ...formData,
      role,
      canManageUsers: role === 'study' ? formData.canManageUsers : false
    };
  }

  function addAssignment() {
    if (formData.assignments.length >= MAX_STUDIES || remainingStudyCount() <= 0) return;
    formData.assignments = [...formData.assignments, emptyAssignment()];
  }

  async function setProtocol(index, protocolNumber) {
    const study = studyOptions.find((row) => row.protocolNumber === protocolNumber);
    const next = [...formData.assignments];
    next[index] = {
      ...next[index],
      protocolNumber,
      studyGUID: study?._id || '',
      siteIds: [],
      siteOptions: [],
      allSites: true,
      siteFilter: '',
      protocolError: protocolNumber && !study ? 'Study not found' : '',
      loadingSites: false
    };
    formData.assignments = next;
    if (study) await loadSitesForRow(index);
  }

  function removeAssignment(index) {
    formData.assignments = formData.assignments.filter((_, i) => i !== index);
  }

  function setAllSites(index, checked) {
    const next = [...formData.assignments];
    next[index] = { ...next[index], allSites: checked, siteIds: checked ? [] : next[index].siteIds };
    formData.assignments = next;
  }

  function toggleSite(index, siteId, checked) {
    const next = [...formData.assignments];
    const ids = new Set(next[index].siteIds);
    if (checked) ids.add(siteId);
    else ids.delete(siteId);
    next[index] = { ...next[index], allSites: false, siteIds: [...ids] };
    formData.assignments = next;
  }

  function setSiteFilter(index, value) {
    const next = [...formData.assignments];
    next[index] = { ...next[index], siteFilter: value };
    formData.assignments = next;
  }

  async function loadSitesForRow(index) {
    const row = formData.assignments[index];
    if (!row) return;
    const protocol = String(row.protocolNumber || '').trim();
    const study = studyOptions.find((s) => s.protocolNumber === protocol);
    const studyGUID = row.studyGUID || study?._id;
    if (!protocol || !studyGUID) {
      const next = [...formData.assignments];
      next[index] = { ...row, siteOptions: [], studyGUID: '', protocolError: protocol ? 'Study not found' : '' };
      formData.assignments = next;
      return;
    }
    const loadingRows = [...formData.assignments];
    loadingRows[index] = { ...row, studyGUID, loadingSites: true, protocolError: '' };
    formData.assignments = loadingRows;
    try {
      const sitesRes = await fetch(`${API_BASE_URL}/sites/study/${studyGUID}`);
      let siteOptionsForRow = [];
      if (sitesRes.ok) {
        const sitesData = await sitesRes.json();
        siteOptionsForRow = Array.isArray(sitesData) ? sitesData : sitesData.data || [];
      }
      const next = [...formData.assignments];
      next[index] = {
        ...next[index],
        studyGUID,
        siteOptions: siteOptionsForRow,
        loadingSites: false,
        protocolError: ''
      };
      formData.assignments = next;
    } catch (_) {
      const next = [...formData.assignments];
      next[index] = {
        ...next[index],
        siteOptions: [],
        loadingSites: false,
        protocolError: 'Could not load sites'
      };
      formData.assignments = next;
    }
  }

  function payloadAssignments() {
    return formData.assignments
      .map((row) => ({
        protocolNumber: String(row.protocolNumber || '').trim(),
        siteIds: row.allSites ? [] : row.siteIds.map((id) => String(id || '').trim()).filter(Boolean)
      }))
      .filter((row) => row.protocolNumber);
  }

  async function handleSubmit() {
    loading = true;
    error = null;
    success = false;

    const needsSites = formData.assignments.some(
      (row) => row.protocolNumber && !row.allSites && !rowLocked(row) && row.siteIds.filter((id) => String(id || '').trim()).length === 0
    );
    if (needsSites) {
      error = 'Select at least one site, or choose All sites.';
      loading = false;
      return;
    }

    try {
      let url, method, requestData;

      if (isEditing) {
        // Update existing user
        url = `${API_BASE_URL}/users/${user._id}`;
        method = 'PUT';
        requestData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          userType: formData.userType,
          state: formData.state,
          country: formData.country,
          sponsor: formData.sponsor,
          assignments: payloadAssignments(),
          canManageUsers: formData.role === 'study' && formData.canManageUsers,
          readOnly: formData.readOnly,
          blinded: formData.blinded
        };
      } else {
        // Create new user
        url = `${API_BASE_URL}/auth/create-user`;
        method = 'POST';
        requestData = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          userType: formData.userType,
          state: formData.state,
          country: formData.country,
          sponsor: formData.sponsor,
          assignments: payloadAssignments(),
          canManageUsers: formData.role === 'study' && formData.canManageUsers,
          readOnly: formData.readOnly,
          blinded: formData.blinded
        };
      }

      console.log(`${isEditing ? 'Updating' : 'Creating'} user:`, requestData);

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData),
      });

      console.log('Response status:', res.status);
      const responseData = await res.json();
      console.log('Response data:', responseData);

      if (!res.ok) {
        throw new Error(
          responseData.error || responseData.details || `Failed to ${isEditing ? 'update' : 'create'} user`
        );
      }

      // Show success message
      success = true;
      
      // Dispatch event to notify parent
      if (isEditing) {
        dispatch('userUpdated', responseData);
        // For editing, dismiss modal after a brief delay to show success message
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        dispatch('userCreated', responseData);
        // For creating, dismiss modal immediately
        loading = false;
        handleClose();
      }
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} user:`, err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  async function fetchOrganizationStatus() {
    if (!user || isCreatingUser) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/users/${user._id}/organizations`);
      if (res.ok) {
        const data = await res.json();
        organizationStatus = data.user.organizations || [];
        showOrganizationSection = organizationStatus.length > 0;
      }
    } catch (err) {
      console.error('Error fetching organization status:', err);
    }
  }

  async function handleBlockInOrganization(orgId, reason = '') {
    if (!user || isCreatingUser) return;
    
    const blockReason = reason || prompt('Enter reason for blocking (optional):');
    if (blockReason === null) return; // User cancelled
    
    try {
      const res = await fetch(`${API_BASE_URL}/users/${user._id}/organizations/${orgId}/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: blockReason })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to block user in organization');
      }

      // Refresh organization status
      await fetchOrganizationStatus();
      
      // Dispatch event to notify parent
      dispatch('userUpdated', { message: 'User blocked in organization' });
    } catch (err) {
      console.error('Error blocking user in organization:', err);
      error = err.message;
    }
  }

  async function handleUnblockInOrganization(orgId) {
    if (!user || isCreatingUser) return;
    
    if (!confirm('Are you sure you want to unblock this user in the organization?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/users/${user._id}/organizations/${orgId}/unblock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to unblock user in organization');
      }

      // Refresh organization status
      await fetchOrganizationStatus();
      
      // Dispatch event to notify parent
      dispatch('userUpdated', { message: 'User unblocked in organization' });
    } catch (err) {
      console.error('Error unblocking user in organization:', err);
      error = err.message;
    }
  }

  function handleClose() {
    loading = false; // Clear loading state when modal closes
    dispatch('close');
  }
</script>

{#if show}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" transition:fade>
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          {isEditing ? 'Edit User' : 'Create User'}
        </h2>
        <button 
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          on:click={handleClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <form on:submit|preventDefault={handleSubmit}>
        <!-- First row: Email, User Type -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label
              for="email"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Email *
            </label>
            <input
              type="email"
              id="email"
              bind:value={formData.email}
              required={!isEditing}
              disabled={isEditing}
              class="form-input h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 disabled:opacity-50"
            />
          </div>

          <div>
            <label
              for="userType"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              User Type
            </label>
            <select
              id="userType"
              bind:value={formData.userType}
              class="form-select h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
            >
              {#if callerIsAdmin}
                <option value="Internal">Internal</option>
              {/if}
              <option value="Site">Site</option>
              <option value="CRA">CRA</option>
              <option value="Sponsor">Sponsor</option>
              {#if callerIsAdmin}
                <option value="Support">Support</option>
              {/if}
            </select>
          </div>
        </div>

        <!-- Second row: Role, Status -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label
              for="role"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Role
            </label>
            <select
              id="role"
              value={formData.role}
              on:change={(event) => setRole(event.target.value)}
              class="form-select h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
            >
              {#if callerIsAdmin}
                <option value="admin">Administrator</option>
              {/if}
              <option value="site">Site User</option>
              <option value="study">Study User</option>
              {#if callerIsAdmin}
                <option value="support">Support User</option>
              {/if}
            </select>
            {#if formData.role === 'study'}
              <label class="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  id="canManageUsers"
                  bind:checked={formData.canManageUsers}
                  class="rounded border-gray-300 dark:border-gray-600"
                />
                Can manage users
              </label>
            {/if}
          </div>

          <div>
            <label
              for="state"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Status
            </label>
            <select
              id="state"
              bind:value={formData.state}
              disabled={!callerIsAdmin}
              class="form-select h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 disabled:opacity-50"
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Blocked</option>
            </select>
          </div>
        </div>

        <!-- User flags: Read only, Blinded -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="flex items-center gap-2">
            <input
              type="checkbox"
              id="readOnly"
              bind:checked={formData.readOnly}
              class="rounded border-gray-300 dark:border-gray-600"
            />
            <label for="readOnly" class="text-sm text-gray-700 dark:text-gray-300">
              Read only
            </label>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="checkbox"
              id="blinded"
              bind:checked={formData.blinded}
              class="rounded border-gray-300 dark:border-gray-600"
            />
            <label for="blinded" class="text-sm text-gray-700 dark:text-gray-300">
              Blinded
            </label>
          </div>
        </div>

        <!-- Third row: First Name, Last Name -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label
              for="firstName"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              bind:value={formData.firstName}
              required
              class="form-input h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
            />
          </div>

          <div>
            <label
              for="lastName"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              bind:value={formData.lastName}
              required
              class="form-input h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
            />
          </div>
        </div>

        <!-- Fourth row: Country, Sponsor -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label
              for="country"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Country
            </label>
            <select
              id="country"
              bind:value={formData.country}
              class="form-select h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
            >
              <option value="">Select a country</option>
              {#each countryOptions as country}
                <option value={country.label}>{country.label}</option>
              {/each}
            </select>
          </div>

          <div>
            <label
              for="sponsor"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Sponsor
            </label>
            <input
              type="text"
              id="sponsor"
              bind:value={formData.sponsor}
              class="form-input h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
            />
          </div>
        </div>

        <hr class="my-6 border-t border-gray-200 dark:border-gray-700" />
        <!-- Study / site assignments: sites are scoped to the protocol above them. -->
        <div class="mb-4">
          <div class="flex justify-between items-center mb-2">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Study assignments
            </label>
            <button
              type="button"
              class="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
              on:click={addAssignment}
              disabled={formData.assignments.length >= MAX_STUDIES || remainingStudyCount() <= 0 || studiesLoading}
            >
              + Add Study
            </button>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
            One row per study. All sites covers every site in that study.
          </p>
          <div class="border border-gray-200 dark:border-gray-600 rounded overflow-hidden">
            {#each formData.assignments as assignment, index (index)}
              {#if !rowLocked(assignment)}
              <div class="p-3 border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                <div class="flex gap-2 items-start">
                  <div class="flex-1 min-w-0">
                      <select
                        class="form-select h-8 w-full border px-2 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
                        value={assignment.protocolNumber}
                        disabled={studiesLoading}
                        on:change={(event) => setProtocol(index, event.target.value)}
                      >
                        <option value="">Select a study</option>
                        {#each protocolOptionsFor(index) as study (study.protocolNumber)}
                          <option value={study.protocolNumber}>
                            {study.protocolNumber}{study.studyName ? ` — ${study.studyName}` : ''}
                          </option>
                        {/each}
                      </select>
                  </div>
                    <button
                      type="button"
                      class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 px-2 h-8"
                      on:click={() => removeAssignment(index)}
                    >
                      ×
                    </button>
                </div>
                {#if assignment.loadingSites}
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-2">Loading sites…</div>
                {:else if assignment.protocolError}
                  <div class="text-xs text-red-600 dark:text-red-400 mt-2">{assignment.protocolError}</div>
                {:else if assignment.protocolNumber}
                  <label class="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={assignment.allSites}
                      on:change={(event) => setAllSites(index, event.target.checked)}
                    />
                    All sites
                  </label>
                  {#if !assignment.allSites}
                    {#if (assignment.siteOptions || []).length > 8}
                      <input
                        type="text"
                        placeholder="Filter sites"
                        value={assignment.siteFilter}
                        on:input={(event) => setSiteFilter(index, event.target.value)}
                        class="form-input h-8 w-full border px-2 rounded text-sm mt-2 dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100"
                      />
                    {/if}
                    <div class="mt-2 max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded">
                      {#each visibleSites(assignment) as site (site.siteId)}
                        <label class="flex items-center gap-2 px-2 py-1 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <input
                            type="checkbox"
                            checked={assignment.siteIds.includes(site.siteId)}
                            on:change={(event) => toggleSite(index, site.siteId, event.target.checked)}
                          />
                          <span>{site.siteId}{site.siteName ? ` — ${site.siteName}` : ''}</span>
                        </label>
                      {:else}
                        <div class="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">No sites in this study</div>
                      {/each}
                    </div>
                  {/if}
                {/if}
              </div>
              {/if}
            {/each}
            {#if formData.assignments.length === 0}
              <div class="p-3 text-sm text-gray-500 dark:text-gray-400 italic">
                {formData.role === 'admin' || formData.role === 'support'
                  ? 'All studies. Add a study to limit access.'
                  : 'No study access'}
              </div>
            {/if}
          </div>
        </div>

      <!-- Organization Management Section (only for editing existing users) -->
      {#if isEditing && showOrganizationSection}
        <hr class="my-6 border-t border-gray-200 dark:border-gray-700" />
        <div class="mb-4">
          <div class="flex items-center mb-2">
            <FontAwesomeIcon icon={faBuilding} class="mr-2 text-gray-600 dark:text-gray-400" />
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Organizations
            </label>
          </div>
          
          <div class="flex flex-wrap gap-2">
            {#each organizationStatus as org}
              <div class="inline-flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-1 rounded-full text-sm">
                <span class="font-medium">{org.organizationId}</span>
                {#if org.isPrimary}
                  <span>(Primary)</span>
                {/if}
                {#if org.blocked}
                  <span>(Blocked)</span>
                {/if}
              </div>
            {/each}
            {#if organizationStatus.length === 0}
              <div class="text-sm text-gray-500 dark:text-gray-400 italic">No organizations assigned</div>
            {/if}
          </div>
        </div>
      {/if}

      {#if error}
          <div class="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md">
            {error}
          </div>
        {/if}

        {#if success}
          <div class="mb-4 p-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md">
            {isEditing ? 'User updated successfully!' : 'User created successfully! They will receive a password reset email to set up their account.'}
          </div>
        {/if}

        <div class="flex mt-6 justify-end space-x-3">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            on:click={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if} 